/**
 * FillBlankQuiz Adult Renderer
 * Thin rendering component that uses the useFillBlankQuiz headless hook for logic.
 */

import { useMemo, type ReactNode } from 'react'
import { cn } from '../../../lib/utils'
import { QuizBlank, NumberKeypad, QuizResultFeedback, SubmitButton, QuizSolveDescription, QuizHeader } from '../shared'
import { formatProblem } from '../../../utils/quiz-parser'
import type { FillBlankQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { useFillBlankQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface FillBlankQuizProps {
  quizId: string | number
  quiz: FillBlankQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  useKeypad?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function FillBlankQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  useKeypad = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: FillBlankQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    answers,
    currentIndex,
    blankCount,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleBlankClick,
    handleInputChange,
    handleKeypadInput,
    handleKeypadClear,
    handleSubmit,
  } = useFillBlankQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // 문제 렌더링 - QuizBlank JSX를 생성하므로 renderer에 위치
  const renderedProblem = useMemo<ReactNode[]>(() => {
    if (blankCount === 0) return []

    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      (index: number, content: string) => {
        const key = content || ''

        return (
          <QuizBlank
            key={`blank-${quizId}-${index}`}
            index={index}
            answers={answers}
            currentIndex={currentIndex}
            isCorrect={isSolved ? solveResult?.corrects?.[index] : null}
            isSolved={isSolved}
            showCorrectAnswer={shouldShowCorrectAnswers}
            correctAnswer={solveResult?.correctAnswers?.[index]}
            onClick={() => handleBlankClick(index)}
            onChange={(value) => handleInputChange(index, value, key)}
            useKeypad={useKeypad}
            isViewSolved={isViewSolved}
          />
        )
      },
      quiz.additionalImages ?? undefined
    )
  }, [blankCount, currentIndex, isSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, useKeypad, isViewSolved, handleBlankClick, handleInputChange, answers, quiz.additionalImages])

  return (
    <div className={cn('space-y-6', useKeypad && !isSolved && 'pb-48')}>
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.fillBlank.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
      />

      {/* 문제 본문 - 기획 데모 .passage-area 스타일 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'p-4 rounded-xl bg-gray-100',
          'text-xl text-gray-700',
          '[&_.formatProblem]:leading-relaxed',
          '[&_span[class*="inline-flex"]]:leading-normal'
        )}
      >
        {renderedProblem}
      </motion.div>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 결과 피드백 */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          correctAnswer={shouldShowCorrectAnswers ? solveResult?.correctAnswers?.join(', ') : undefined}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* 해설 */}
      {isSolved && solveResult?.description && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult.description}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}

      {/* 숫자 키패드 (useKeypad가 true일 때만) */}
      {useKeypad && !isSolved && !isViewSolved && (
        <NumberKeypad
          onInput={handleKeypadInput}
          onClear={handleKeypadClear}
          disabled={isSolved}
        />
      )}
    </div>
  )
}
